/**
 * The web chart contract, `schema_version` 1.0.
 *
 * Mirrors `web_contract.py` in the quantist_api repository, which is the
 * source of truth. Keep the two in step: a field renamed there is a breaking
 * change here, which is why the payload carries a version and the client
 * checks it.
 *
 * Units, because the contract deliberately ships raw numbers and never
 * preformatted strings:
 *   - `proportion`, `net_proportion`, `scripless_ratio`, holding shares: 0..1
 *   - `price_correlation`, `ma_price_correlation`: -1..1
 *   - `close`/`vwap`/profile boundaries: price, in the instrument's currency
 *   - `money_flow`, `value_flow`, `net_value`: IDR
 * Anything the backend could not compute is `null`, never NaN and never 0.
 */

export type AnalysisMethod = "foreign" | "broker";
export type PowerLabel = "low" | "medium" | "high";
/** A gap in a series: the backend had no value, so the chart must not draw one. */
export type Maybe = number | null;

export interface Instrument {
	code: string;
	symbol: string;
	display_name: string;
	kind: "index" | "stock";
	aliases: string[];
}

export interface Period {
	start: string;
	end: string;
	bars: number;
	params: Record<string, number | null>;
}

export interface Summary {
	date: string;
	close: Maybe;
	vwap: Maybe;
	money_flow: Maybe;
	value_flow: Maybe;
	proportion: Maybe;
	net_proportion: Maybe;
	price_correlation: Maybe;
	ma_price_correlation: Maybe;
	power: number | null;
	power_label: PowerLabel | null;
	method: AnalysisMethod;
}

/** Column-oriented OHLC. Every list is aligned index-for-index with `dates`. */
export interface Price {
	dates: string[];
	open: Maybe[];
	high: Maybe[];
	low: Maybe[];
	close: Maybe[];
	volume: Maybe[];
	value: Maybe[];
}

/** Named layers, also aligned to `price.dates`. */
export interface Indicators {
	vwap: Maybe[];
	value_flow: Maybe[];
	proportion: Maybe[];
	net_proportion: Maybe[];
	net_value: Maybe[];
}

export interface ProfileZone {
	low: number;
	high: number;
	mid: number;
	net_value: Maybe;
	is_peak: boolean;
}

export interface HoldingComposition {
	periods: string[];
	foreign: Maybe[];
	local_institutional: Maybe[];
	local_individual: Maybe[];
	scripless_ratio: Maybe[];
}

export interface Clustering {
	n_selected_cluster: number | null;
	correlation: Maybe;
	selected_brokers: string[];
}

export interface Annotations {
	method: AnalysisMethod;
	method_label: string;
	/** "W" for whale/broker flow, "F" for foreign flow. Drives every series label. */
	abbrev: string;
	power_label: PowerLabel | null;
	clustering: Clustering | null;
}

export interface Meta {
	schema_version: string;
	generated_at: string;
	source: string;
}

export interface WebChart {
	schema_version: string;
	instrument: Instrument;
	period: Period;
	summary: Summary;
	price: Price;
	indicators: Indicators;
	profile_zones: ProfileZone[];
	holding_composition: HoldingComposition | null;
	annotations: Annotations;
	meta: Meta;
}

export const SUPPORTED_SCHEMA_MAJOR = "1";

/**
 * Structural check on an untrusted payload.
 *
 * Not a full validator: it is the smallest set of shape guarantees the chart
 * adapter assumes, so a wrong or truncated response fails at the boundary with
 * a readable message instead of somewhere inside ECharts.
 */
export function assertWebChart(value: unknown): asserts value is WebChart {
	const payload = value as Partial<WebChart> | null;
	if (!payload || typeof payload !== "object") throw new Error("Chart payload is not an object.");

	for (const section of ["instrument", "period", "summary", "price", "indicators", "annotations", "meta"] as const) {
		if (!payload[section]) throw new Error(`Chart payload is missing the "${section}" section.`);
	}

	const major = String(payload.schema_version ?? "").split(".")[0];
	if (major !== SUPPORTED_SCHEMA_MAJOR) {
		throw new Error(`Unsupported chart schema_version "${payload.schema_version}"; this build reads ${SUPPORTED_SCHEMA_MAJOR}.x.`);
	}

	const { dates, open, high, low, close } = payload.price as Price;
	if (!Array.isArray(dates) || dates.length === 0) throw new Error("Chart payload has no price dates.");
	for (const [name, series] of Object.entries({ open, high, low, close, ...payload.indicators })) {
		if (!Array.isArray(series) || series.length !== dates.length) {
			throw new Error(`Series "${name}" is not aligned with price.dates (${dates.length} bars).`);
		}
	}
}
